import api from '../lib/api';
import type { Paginated } from '../types';
import type {
  Opportunity,
  OpportunityClient,
  OpportunityClientInput,
  OpportunityInput,
  OpportunityProduct,
  OpportunityProductInput,
} from '../types/crm';

// Single-record endpoints (including nested products/clients) return
// `{ data: { id, type, attributes: {...} } }`; list endpoints return flat
// records. See operator.service.ts for the rationale.

export const opportunityService = {
  getAll: async (params?: Record<string, unknown>): Promise<Paginated<Opportunity>> => {
    const response = await api.get<Paginated<Opportunity>>('/opportunities', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Opportunity> => {
    const response = await api.get<{ data: { attributes: Opportunity } }>(`/opportunities/${id}`);
    return response.data.data.attributes;
  },

  create: async (data: OpportunityInput): Promise<Opportunity> => {
    const response = await api.post<{ data: { attributes: Opportunity } }>('/opportunities', {
      opportunity: data,
    });
    return response.data.data.attributes;
  },

  update: async (id: number, data: Partial<OpportunityInput>): Promise<Opportunity> => {
    const response = await api.patch<{ data: { attributes: Opportunity } }>(`/opportunities/${id}`, {
      opportunity: data,
    });
    return response.data.data.attributes;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/opportunities/${id}`);
  },

  // Nested product line items
  addProduct: async (
    opportunityId: number,
    data: OpportunityProductInput,
  ): Promise<OpportunityProduct> => {
    const response = await api.post<{ data: { attributes: OpportunityProduct } }>(
      `/opportunities/${opportunityId}/opportunity_products`,
      { opportunity_product: data },
    );
    return response.data.data.attributes;
  },

  updateProduct: async (
    opportunityId: number,
    productLineId: number,
    data: Partial<OpportunityProductInput>,
  ): Promise<OpportunityProduct> => {
    const response = await api.patch<{ data: { attributes: OpportunityProduct } }>(
      `/opportunities/${opportunityId}/opportunity_products/${productLineId}`,
      { opportunity_product: data },
    );
    return response.data.data.attributes;
  },

  removeProduct: async (opportunityId: number, productLineId: number): Promise<void> => {
    await api.delete(`/opportunities/${opportunityId}/opportunity_products/${productLineId}`);
  },

  // Nested named clients
  addClient: async (
    opportunityId: number,
    data: OpportunityClientInput,
  ): Promise<OpportunityClient> => {
    const response = await api.post<{ data: { attributes: OpportunityClient } }>(
      `/opportunities/${opportunityId}/opportunity_clients`,
      { opportunity_client: data },
    );
    return response.data.data.attributes;
  },

  removeClient: async (opportunityId: number, clientId: number): Promise<void> => {
    await api.delete(`/opportunities/${opportunityId}/opportunity_clients/${clientId}`);
  },
};

export default opportunityService;
